<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue";
  import { useRoute } from "vue-router";
  import HelpData from "~/assets/data/help-result-v2";
  import MarkdownIt from "~/components/MarkdownIt.vue";
  import Feedback from "~/components/help/Feedback.vue";
  import SearchSuggest from "~/components/help/SearchSuggest.vue";
  import Common from "~/utils/common";

  definePageMeta({
    layout: "nav-layout",
  });

  useHead({
    title: "帮助中心",
    titleTemplate: "帮助中心 - %s",
  });

  const api = useApi();
  const route = useRoute();

  const container = ref<HTMLDivElement | null>(null);
  const content = ref<HTMLDivElement | null>(null);

  const keyword = ref("");
  const list = ref(Common.clone(HelpData));
  const activeKey = ref("");
  const id = ref("");
  const subId = ref("");
  const state = ref({ feedback: false });
  const viewerOptions = ref({
    navbar: false,
    title: false,
    toolbar: false,
  });

  const paths = computed(() => {
    if (id.value) {
      const data = Common.clone(HelpData);
      return Common.findNodesPathObject(data, id.value, "id");
    } else if (state.value.feedback) {
      return [{ title: "意见反馈" }];
    }
    return [];
  });

  const doc = computed(() => {
    const data = Common.clone(HelpData);
    const list = data.find((item: any) => item.id === id.value);
    if (list) {
      list.children = Common.treeToArray(list.children);
    }
    return list;
  });

  // const el = computed(() => container.value);

  watch(route, (to) => {
    if (to.path === "/help/result") {
      const val = to.hash;
      if (val) {
        goto(val);
      }
    }
  });

  onMounted(() => {
    const { feedback } = route.query;
    if (feedback) {
      state.value.feedback = true;
      return;
    }
    const val = location.hash;
    if (val) {
      goto(location.hash);
    } else {
      if (HelpData.length > 0) location.hash = `#${HelpData[0].id}`;
    }
    api.site();
  });

  function onDocClick(docId: string) {
    location.href = `/help/result#${docId}`;
  }

  function goto(hash: string) {
    state.value.feedback = false;
    const value = hash.replace("#", "");
    activeKey.value = value;
    id.value = value.substring(0, 3);
    if (value.length > 3) {
      subId.value = value.substring(0, 6);
    } else {
      subId.value = "";
    }
    setTimeout(() => {
      const elem = document.getElementById(`l${value}`);
      if (elem) {
        elem.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 300);
  }

  function filter(list: any[]) {
    return list.filter((item) => item.title);
  }
</script>

<template>
  <div ref="container" class="container">
    <div class="content-wrapper">
      <div class="catalogue">
        <div class="catalogue-title">所有文章</div>
        <div class="catalogue-menus">
          <a-collapse v-model:active-key="id" :accordion="true" :bordered="false">
            <template #expandIcon="props">
              <icon-font type="shouqi" :rotate="props?.isActive ? 90 : 0" />
              <!-- <Icon name="custom:shouqi" :rotate="props?.isActive ? 90 : 0" />-->
            </template>
            <a-collapse-panel v-for="item in list" :key="item.id">
              <template #header>
                <div class="group-item" :class="{ active: item.id === activeKey }" @click.stop="onDocClick(item.id)">
                  <span :title="item.title">{{ item.title }}</span>
                </div>
              </template>
              <div class="items">
                <div v-for="(child, j) in filter(item.children)" :key="j" class="item">
                  <template v-if="child.children && child.children.length > 0">
                    <div class="menu-children">
                      <a-collapse v-model:active-key="subId" :accordion="true" :bordered="false">
                        <template #expandIcon="subProps">
                          <icon-font type="shouqi" :rotate="subProps?.isActive ? 90 : 0" />
                          <!-- <Icon name="custom:shouqi" :rotate="cprops.isActive ? 90 : 0" />-->
                        </template>
                        <a-collapse-panel :key="child.id">
                          <template #header>
                            <div
                              class="group-item"
                              :class="{ active: child.id === activeKey }"
                              @click.stop="onDocClick(child.id)">
                              <span :title="child.title">{{ child.title }}</span>
                            </div>
                          </template>
                          <div
                            v-for="(cc, k) in filter(child.children)"
                            :key="k"
                            class="leaf"
                            :class="{ active: cc.id === activeKey }"
                            @click="onDocClick(cc.id)">
                            <icon-font type="wenben" />
                            <!-- <Icon name="custom:wenben" />-->
                            <span class="title" :title="cc.title">{{ cc.title }}</span>
                          </div>
                        </a-collapse-panel>
                      </a-collapse>
                    </div>
                  </template>
                  <div v-else class="leaf" :class="{ active: child.id === activeKey }" @click="onDocClick(child.id)">
                    <icon-font type="wenben" />
                    <!-- <Icon name="custom:wenben" />-->
                    <span class="title" :title="child.title">{{ child.title }}</span>
                  </div>
                </div>
              </div>
            </a-collapse-panel>
          </a-collapse>
        </div>
        <div class="catalogue-title">其他</div>
        <div class="catalogue-items">
          <nuxt-link
            class="catalogue-item"
            :class="{ active: state.feedback }"
            to="/help/result?feedback=1"
            aria-label="意见反馈">
            <icon-font type="tishi-687aah25" theme="filled" />
            <!-- <Icon name="custom:tishi-687aah25" theme="filled" />-->
            <span>意见反馈</span>
          </nuxt-link>
        </div>
      </div>
      <div ref="content" class="content">
        <div class="header">
          <a-breadcrumb class="breadcrumb" separator=">">
            <a-breadcrumb-item>
              <nuxt-link to="/help/">帮助中心</nuxt-link>
            </a-breadcrumb-item>
            <a-breadcrumb-item v-for="(item, i) in paths" :key="i">
              {{ item.title }}
            </a-breadcrumb-item>
          </a-breadcrumb>
          <SearchSuggest v-if="!state.feedback" :model-value="keyword" :get-popup-container="() => content">
            <a-input v-model:value="keyword" class="input" placeholder="请输入关键词，比如：合同">
              <template #prefix>
                <icon-font type="sousuo1" />
                <!-- <Icon name="custom:sousuo1" />-->
              </template>
            </a-input>
          </SearchSuggest>
        </div>
        <div v-if="doc" v-viewer="viewerOptions" class="main">
          <div v-for="(item, i) in doc.children" :key="i" class="item">
            <div :id="`l${item.id}`" class="offset"></div>
            <div :id="`l${item.id}`" class="title">
              {{ item.title }}
            </div>
            <div class="text">
              <MarkdownIt :model-value="item.content" />
            </div>
          </div>
        </div>
        <div v-if="state.feedback" class="feedback-wrap">
          <Feedback />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
  .container {
    min-height: 100%;
    background: #fff;

    &::-webkit-scrollbar {
      width: 0;
      height: 0;
    }

    .content-wrapper {
      position: relative;
      display: flex;
      width: 1600px;
      margin: 0 auto;
    }
  }

  .catalogue {
    position: sticky;
    top: 60px;
    min-width: 380px;
    max-width: 380px;
    height: calc(100vh - 60px);
    padding-top: 14px;
    padding-bottom: 120px;
    overflow-y: auto;
    border-right: #dce5ec solid 1px;

    &::-webkit-scrollbar {
      width: 0;
      height: 0;
    }

    .catalogue-title {
      display: flex;
      align-items: center;
      height: 60px;
      padding-left: 44px;
      font-size: 20px;
      color: #1d2129;
    }

    .catalogue-items {
      padding: 0 28px;
    }

    .catalogue-item {
      display: block;
      height: 50px;
      padding-left: 20px;
      font-size: 16px;
      line-height: 50px;
      color: #1d2129;
      cursor: pointer;
      border-radius: 7px;

      .anticon {
        margin-right: 7px;
        color: #7a869a;
      }

      &.active {
        color: #2f78ff;
        background: #e1ebff;

        .anticon {
          color: #2f78ff;
        }
      }
    }

    :deep(.ant-collapse) {
      padding: 0 28px;
      background: #fff;

      .ant-collapse-item {
        border: 0;

        .ant-collapse-header {
          position: relative;
          height: 50px;

          .ant-collapse-expand-icon {
            left: 10px;
            z-index: 2;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            font-size: 12px;

            .anticon {
              font-size: 12px !important;
            }
          }

          .group-item {
            position: absolute;
            top: 0;
            left: 0;
            display: inline-block;
            width: 100%;
            height: 50px;
            padding-right: 16px;
            padding-left: 36px;
            overflow: hidden;
            font-size: 16px;
            line-height: 50px;
            text-overflow: ellipsis;
            white-space: nowrap;
            border-radius: 8px;

            &.active {
              color: var(--primary-color);
              background: #e1ebff;
            }
          }
        }

        .ant-collapse-content {
          .ant-collapse-content-box {
            padding: 0;

            .items {
              .item {
                color: #1d2129;

                .leaf {
                  display: flex;
                  align-items: center;
                  width: 100%;
                  padding: 14px 0;
                  padding-left: 36px;
                  font-size: 16px;
                  cursor: pointer;
                  border-radius: 8px;

                  &.active {
                    color: var(--primary-color);
                    background: #e1ebff;
                  }

                  .title {
                    display: block;
                    line-height: 22px;
                  }

                  .anticon {
                    align-self: baseline;
                    margin-right: 8px;
                    font-size: 20px;
                    transform: translateY(1px);
                  }
                }

                .menu-children {
                  .ant-collapse-header {
                    .ant-collapse-arrow {
                      left: 5px;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  .content {
    position: relative;
    flex-grow: 1;
    padding-right: 60px;
    padding-bottom: 120px;
    padding-left: 60px;

    .header {
      display: flex;
      justify-content: space-between;
      padding: 20px 0;
      border-bottom: #dce5ec solid 1px;

      .breadcrumb {
        font-size: 16px;
        line-height: 48px;
        color: #7a869a;

        & > span:last-child a {
          color: #1d2129;
        }
      }

      :deep(.input) {
        width: 400px;

        .ant-input-prefix {
          left: 18px;

          .anticon {
            font-size: 24px;
            color: #b6c4d1;
          }
        }

        .ant-input {
          height: 48px;
          padding-left: 56px;
          font-size: 16px;
          border-color: #dce5ec;
          border-radius: 8px;
          box-shadow: none;
          transition: none;
        }
      }
    }

    .main {
      position: relative;
      padding-right: 150px;
      color: #1d2129;

      .title {
        padding: 20px 0;
        font-size: 24px;
        font-weight: 600;
        line-height: 56px;
      }

      .link-item {
        margin-bottom: 24px;

        .link {
          font-size: 20px;
          color: #1d2129;
          transition: none;

          &:hover {
            color: var(--primary-color);
            text-decoration: underline;
          }
        }
      }
    }
  }
</style>
